import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, Wallet as WalletIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchWalletById, updateWallet } from '../walletApi';
import { useTranslation } from 'react-i18next';

export default function EditWallet() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true,
    image: ''
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const loadWallet = async () => {
      try {
        const wallet = await fetchWalletById(id);
        setFormData({
          name: wallet.Name || '',
          description: wallet.Description || '',
          isActive: wallet.IsActive !== undefined ? wallet.IsActive : true,
          image: ''
        });
        setCurrentImage(wallet.ImageUrl || null);
      } catch (error) {
        console.error('Error loading wallet:', error);
        setNotification({ open: true, message: t('WalletLoadFail') || 'Failed to load wallet', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadWallet();
  }, [id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result });
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateWallet(id, formData);
      setNotification({ open: true, message: t('WalletUpdateSuccess') || 'Wallet updated successfully', severity: 'success' });
      setTimeout(() => navigate('/admin/wallets'), 1500);
    } catch (error) {
      console.error('Error updating wallet:', error);
      setNotification({ open: true, message: t('WalletUpdateFail') || 'Failed to update wallet', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WalletIcon fontSize="large" color="primary" />
          {t('EditWallet') || 'Edit Wallet'}
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/wallets')}>
          {t('BackToList')}
        </Button>
      </Box>

      <Card sx={{ boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t('WalletName') || 'Wallet Name'}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />

              <TextField
                fullWidth
                label={t('Description')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('WalletImage') || 'Wallet Image'}
                </Typography>
                <Button variant="contained" component="label">
                  {t('UploadImage')}
                  <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                </Button>
                {imagePreview && (
                  <Box mt={2}>
                    <img src={imagePreview} alt="Preview" style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8 }} />
                  </Box>
                )}
                {!imagePreview && currentImage && (
                  <Box mt={2}>
                    <img src={currentImage} alt="Current" style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8 }} />
                  </Box>
                )}
              </Box>

              <FormControlLabel
                control={<Checkbox checked={formData.isActive} onChange={handleChange} name="isActive" color="success" />}
                label={<Typography fontWeight="medium">{t('ActiveStatus')}</Typography>}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/admin/wallets')}>{t('Cancel')}</Button>
                <Button type="submit" variant="contained" disabled={saving} startIcon={<SaveIcon />}>
                  {saving ? t('Updating') : t('UpdateWallet') || 'Update Wallet'}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <Snackbar open={notification.open} autoHideDuration={6000} onClose={() => setNotification({ ...notification, open: false })}>
        <Alert severity={notification.severity}>{notification.message}</Alert>
      </Snackbar>
    </Box>
  );
}
